import { useState, useCallback, useEffect } from 'react';
import { AccountService, PasswordChangeResult } from '../services/AccountService';
import { validatePassword } from '../utils/accountValidation';

export interface UsePasswordChangeReturn {
  changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<void>;
  validatePasswordStrength: (password: string) => { isValid: boolean; errors: string[] };
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  validationErrors: {
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  };
  clearError: () => void;
  clearSuccess: () => void;
  clearValidationErrors: () => void;
}

/**
 * Custom hook for password change operations with validation
 * Requirements: 3.1 - Password change with validation
 */
export function usePasswordChange(): UsePasswordChangeReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const validatePasswordStrength = useCallback((password: string) => {
    const validation = validatePassword(password);
    return {
      isValid: validation.isValid,
      errors: validation.isValid ? [] : [validation.error || 'Invalid password']
    };
  }, []);

  const changePassword = useCallback(async (
    currentPassword: string, 
    newPassword: string, 
    confirmPassword: string
  ) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    setValidationErrors({});

    try {
      // Validate inputs
      const errors: typeof validationErrors = {};

      if (!currentPassword || currentPassword.trim().length === 0) {
        errors.currentPassword = 'Current password is required';
      }

      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.isValid) {
        errors.newPassword = passwordValidation.error || 'Invalid password';
      }

      if (newPassword !== confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }

      if (currentPassword === newPassword) {
        errors.newPassword = 'New password must be different from current password';
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        throw new Error('Validation failed');
      }

      const result: PasswordChangeResult = await AccountService.changePassword(
        currentPassword,
        newPassword
      );

      if (result.success) {
        setSuccess(result.message || 'Password successfully updated');
      } else {
        // Handle specific error cases
        if (result.message.toLowerCase().includes('current password')) {
          setValidationErrors({ currentPassword: result.message });
        } else {
          setError(result.message);
        }
        throw new Error(result.message);
      }
    } catch (err) {
      if (err instanceof Error && err.message !== 'Validation failed') {
        const errorMessage = err.message || 'Failed to change password';
        setError(errorMessage);
      } else if (typeof err === 'string') {
        setError('Failed to change password');
      } else if (err && typeof err === 'object' && !(err instanceof Error)) {
        setError('Failed to change password');
      }
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSuccess = useCallback(() => {
    setSuccess(null);
  }, []);

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  return {
    changePassword,
    validatePasswordStrength,
    isSubmitting,
    error,
    success,
    validationErrors,
    clearError,
    clearSuccess,
    clearValidationErrors
  };
}